
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Template {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  likes: number;
  copies: number;
}

interface CategoryTemplates {
  [categoryName: string]: Template[];
}

export const useTemplatesByCategory = () => {
  const [templates, setTemplates] = useState<CategoryTemplates>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const productCategories = [
    'Product Strategy',
    'Product Discovery', 
    'Product Design',
    'Product Requirements Document (PRD)',
    'Product Launch',
    'Product Marketing',
    'Product Analytics',
    'Product Frameworks',
    'User Feedback',
    'User Stories',
    'Stakeholder Management',
    'Market Research and Competitor Analysis'
  ];

  useEffect(() => {
    const fetchTemplatesByCategory = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const categoryTemplates: CategoryTemplates = {};
        
        // First, get all categories that match our product category names
        const { data: categories, error: categoriesError } = await supabase
          .from('categories')
          .select('id, name, slug')
          .in('name', productCategories);

        if (categoriesError) {
          console.error('Error fetching categories:', categoriesError);
        }

        // Create a map of category names to IDs
        const categoryMap = new Map();
        categories?.forEach(cat => {
          categoryMap.set(cat.name, cat.id);
        });

        // For each product category, fetch templates
        for (const categoryName of productCategories) {
          const categoryId = categoryMap.get(categoryName);
          
          if (categoryId) {
            // Fetch templates by exact category ID match
            const { data: exactMatches, error: exactError } = await supabase
              .from('prompts')
              .select('id, title, description, content, likes, copies')
              .eq('category_id', categoryId)
              .eq('status', 'approved')
              .limit(20);

            if (exactError) {
              console.error(`Error fetching exact matches for ${categoryName}:`, exactError);
            }

            if (exactMatches && exactMatches.length > 0) {
              categoryTemplates[categoryName] = exactMatches.map(item => ({
                id: item.id,
                title: item.title,
                description: item.description,
                content: item.content,
                category: categoryName,
                likes: item.likes || 0,
                copies: item.copies || 0
              }));
            }
          }
          
          // If no exact matches or no category found, try fuzzy search as fallback
          if (!categoryTemplates[categoryName] || categoryTemplates[categoryName].length === 0) {
            const { data: fuzzyMatches, error: fuzzyError } = await supabase
              .from('prompts')
              .select(`
                id,
                title,
                description,
                content,
                likes,
                copies
              `)
              .eq('status', 'approved')
              .or(`title.ilike.%${categoryName}%,description.ilike.%${categoryName}%`)
              .limit(10);

            if (fuzzyError) {
              console.error(`Error fetching fuzzy matches for ${categoryName}:`, fuzzyError);
            }

            if (fuzzyMatches && fuzzyMatches.length > 0) {
              categoryTemplates[categoryName] = fuzzyMatches.map(item => ({
                id: item.id,
                title: item.title,
                description: item.description,
                content: item.content,
                category: categoryName,
                likes: item.likes || 0,
                copies: item.copies || 0
              }));
            } else {
              // Ensure category exists even if empty
              categoryTemplates[categoryName] = [];
            }
          }
        }
        
        setTemplates(categoryTemplates);
      } catch (err: any) {
        console.error('Error fetching categorized templates:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplatesByCategory();
  }, []);

  return { templates, isLoading, error };
};
